import React from 'react';
import {
    cellsToZoneCounts,
    collectGkZoneStatsForPlayer,
    GK_SITUATION_KEYS,
    GK_SITUATION_LABELS,
    GkSituationKey,
    isAllSituations,
    mapLocationsFacedByPlayer,
    mapTargetsFacedByPlayer,
    savePctText,
} from './insightsGoalkeeperZones';
import {
    collectBundleFilterOptions,
    filterMatchBundles,
    MatchBundleFilterSelection,
} from './insightsMatchBundleFilters';
import { MapPeriodKey } from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';
import {
    ShotFakeKey,
    ShotFoulKey,
    ShotKindFilter,
    shotKindFilterLabel,
    ShotTechniqueKey,
} from './insightsShotKindFilters';
import InsightsSituationMapsShell from './InsightsSituationMapsShell';

interface Props {
    bundles: MatchBundle[];
    ourTeamIds: number[];
    playerId: number;
    /** Parent already applied competition/date/match - hide duplicate scope filters. */
    hideScopeFilters?: boolean;
}

/**
 * Goalkeeper maps on the player page: one goalmouth + one half-pool,
 * filtered by situation chips (Extra player / 6 to 6 / Penalties / All).
 * Keeper colours (goal red / save green). TW GK attribution.
 */
const InsightsGkPlayerMaps: React.FC<Props> = ({
    bundles,
    ourTeamIds,
    playerId,
    hideScopeFilters,
}) => {
    const [selected, setSelected] = React.useState<GkSituationKey[]>([...GK_SITUATION_KEYS]);
    const [periodSelected, setPeriodSelected] = React.useState<MapPeriodKey[] | null>(null);
    const [techniqueSelected, setTechniqueSelected] = React.useState<ShotTechniqueKey[] | null>(
        null
    );
    const [foulSelected, setFoulSelected] = React.useState<ShotFoulKey[] | null>(null);
    const [fakeSelected, setFakeSelected] = React.useState<ShotFakeKey[] | null>(null);
    const [showZoneStats, setShowZoneStats] = React.useState(true);
    const [showShotMarks, setShowShotMarks] = React.useState(true);
    // null = all in that dimension; [] = none (Clear).
    const [bundleFilters, setBundleFilters] = React.useState<MatchBundleFilterSelection>({
        competitionIds: null,
        dateFrom: null,
        dateTo: null,
        matchIds: null,
    });

    const bundleFilterOptions = React.useMemo(
        () => collectBundleFilterOptions(bundles, ourTeamIds),
        [bundles, ourTeamIds]
    );

    const sampleBundles = React.useMemo(
        () => filterMatchBundles(bundles, bundleFilters),
        [bundles, bundleFilters]
    );

    const overall = React.useMemo(
        () => collectGkZoneStatsForPlayer(bundles, ourTeamIds, playerId),
        [bundles, ourTeamIds, playerId]
    );

    const sampleOverall = React.useMemo(
        () => collectGkZoneStatsForPlayer(sampleBundles, ourTeamIds, playerId),
        [sampleBundles, ourTeamIds, playerId]
    );

    const kind: ShotKindFilter = React.useMemo(
        () => ({
            techniques: techniqueSelected,
            fouls: foulSelected,
            fakes: fakeSelected,
        }),
        [techniqueSelected, foulSelected, fakeSelected]
    );

    const filtered = React.useMemo(() => {
        const options = { situations: selected, periodKeys: periodSelected, kind };
        const stats = collectGkZoneStatsForPlayer(sampleBundles, ourTeamIds, playerId, options);
        return {
            stats,
            keeper: stats.keepers[0],
            goalMap: mapTargetsFacedByPlayer(sampleBundles, ourTeamIds, playerId, options),
            poolMap: mapLocationsFacedByPlayer(sampleBundles, ourTeamIds, playerId, options),
        };
    }, [sampleBundles, ourTeamIds, playerId, selected, periodSelected, kind]);

    const overallKeeper = overall.keepers[0];
    if (!overallKeeper || overallKeeper.sog === 0) return null;

    const sampleKeeper = sampleOverall.keepers[0];
    const keeper = filtered.keeper;
    const sog = keeper?.sog ?? 0;

    const situationLabel = isAllSituations(selected)
        ? GK_SITUATION_LABELS.all
        : selected.map((key) => GK_SITUATION_LABELS[key]).join(' + ');
    const kindLabel = shotKindFilterLabel(kind);
    const filterLabel = [situationLabel, kindLabel].filter(Boolean).join(' · ');

    return (
        <InsightsSituationMapsShell
            title="Goalkeeper"
            hint="Save % = saves / (saves + goals against) · green = save · red = goal"
            colorMode="keeper"
            selected={selected}
            onSelectedChange={setSelected}
            periodSelected={periodSelected}
            onPeriodSelectedChange={setPeriodSelected}
            showZoneStats={showZoneStats}
            onShowZoneStatsChange={setShowZoneStats}
            showShotMarks={showShotMarks}
            onShowShotMarksChange={setShowShotMarks}
            bundleFilterOptions={bundleFilterOptions}
            bundleFilters={bundleFilters}
            onBundleFiltersChange={setBundleFilters}
            hideScopeFilters={hideScopeFilters}
            techniqueSelected={techniqueSelected}
            onTechniqueSelectedChange={setTechniqueSelected}
            foulSelected={foulSelected}
            onFoulSelectedChange={setFoulSelected}
            fakeSelected={fakeSelected}
            onFakeSelectedChange={setFakeSelected}
            statsStrip={
                <div className="insightsStatStrip">
                    <div className="insightsStat">
                        <b>
                            {savePctText(sampleKeeper?.saves ?? 0, sampleKeeper?.goalsAgainst ?? 0)}
                        </b>
                        <i>Save %</i>
                    </div>
                    <div className="insightsStat">
                        <b>{sampleKeeper?.sog ?? 0}</b>
                        <i>SOG faced</i>
                    </div>
                    <div className="insightsStat">
                        <b>{sampleKeeper?.saves ?? 0}</b>
                        <i>Saves</i>
                    </div>
                    <div className="insightsStat">
                        <b>{sampleKeeper?.goalsAgainst ?? 0}</b>
                        <i>Goals against</i>
                    </div>
                </div>
            }
            sampleNote={
                sampleKeeper?.sampleWarning ? (
                    <p className="insightsTableNote">
                        Small sample ({sampleKeeper.sog} SOG) - treat % carefully.
                    </p>
                ) : null
            }
            filterSummary={
                sog > 0 && keeper ? (
                    <span>
                        {filterLabel}: {savePctText(keeper.saves, keeper.goalsAgainst)} ·{' '}
                        {keeper.saves}Sv · {keeper.goalsAgainst}G · {keeper.sog} SOG
                    </span>
                ) : (
                    <span>{filterLabel}: no attributed SOG in this sample</span>
                )
            }
            goalMap={filtered.goalMap}
            poolMap={filtered.poolMap}
            goalZoneCounts={keeper ? cellsToZoneCounts(keeper.cells) : undefined}
            poolZoneCounts={keeper ? cellsToZoneCounts(keeper.poolCells) : undefined}
            goalEmptyHint="No goalmouth targets on attributed saves/goals."
            poolEmptyHint="No pool locations on attributed saves/goals."
        />
    );
};

export default InsightsGkPlayerMaps;
